import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import GVCartIcon from "@tSurya/Icons/GVCartIcon";
import MGBWCartIcon from "@tSurya/Icons/MGBWCartIcon";
import { ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import { getActiveThemeId, isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    userRole: state.context.session.userRoles
});

const mapDispatchToProps = {
    loadCurrentCart,
    setNavDrawerIsOpen
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    cartTextLinkStyles?: InjectableCss;
    cartLinkStyle?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    cartTextLinkStyles: {
        css: css`
            ${isThemeActive(THEME_IDS.GLOBAL_VIEWS)
                ? `
                    line-height: 24px;
                `
                : ``
            }
            @media (min-width: 992px) and (max-width: 1166px) {
                display: none !important;
            }
        `,
    },
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? 20 : 24,
                css: css`
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    margin-right: 0;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        );
                    }}
                `
            },
        },
        color: "secondary.contrast",
        css: css`
            position: relative;
            height: ${ELEMENTS.cartLink.height};
            padding: 0 4px 0 0;
            > span {
                color: ${getColor("text.main")} !important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.cartLink.weight};
                font-size: ${TYPOGRAPHY.cartLink.size};
                line-height: ${TYPOGRAPHY.lineHeight.ssmall};
                text-transform: ${TYPOGRAPHY.cartLink.textTransform};

                > span {
                    text-transform: ${TYPOGRAPHY.cartLink.textTransform};
                }
            };
            &.disabled {
                pointer-events: none; 
                opacity: 0.5; 
                cursor: default; 
            }
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    desktopCountText: {
        css: css`
            background: ${getColor("primary")};
            color: ${getColor("primary.contrast")};
            width: auto;
            min-width: 18px;
            padding: 0px 4px;
            height: 18px;
            display: block;
            border-radius: 10px;
            text-align: center;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: 700;
            font-size: 12px;
            line-height: 19px;
            position: absolute;
            left: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '12px' : '16px'};
            top: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '-8px' : '2px'};
        `,
    },
    mobileCountText: {
        css: css`
            background: ${getColor("primary")};
            color: ${getColor("primary.contrast")};
            width: auto;
            min-width: 18px;
            padding: 0px 4px;
            height: 18px;
            display: block;
            border-radius: 10px;
            text-align: center;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: 700;
            font-size: 12px;
            line-height: 19px;
            position: absolute;
            left: auto;
            top: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '-10px' : '4px'};
            right: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '2px' : '7px'};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            top: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '-10px' : '4px'};
                            right: ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) || isThemeActive(THEME_IDS.MGBW) ? '-8px' : '-5px'};
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: "cart" | "truck" | "bag" | "none";
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    userRole,
    setNavDrawerIsOpen
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const getCartIcon = () => {
        const activeThemeId = getActiveThemeId();
        
        switch (activeThemeId) {
            case THEME_IDS.GLOBAL_VIEWS:
                return GVCartIcon;
            case THEME_IDS.MGBW:
                return MGBWCartIcon;
            default:
                return ShoppingCart;
        }
    };

    const cartIcon = getCartIcon();

    const iconMap = {
        cart: cartIcon,
        truck: Truck,
        bag: ShoppingBag,
        none: undefined,
    } as const;

    type IconMapKeys = keyof typeof iconMap;
    const icon = iconMap[(showIcon && selectedIcon as IconMapKeys) || "none"];

    const { routerLink } = styles;
    const cartLink=()=>{
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
    }

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
                
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay || 0}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    return (
        <Link
            {...routerLink}
            href={cartUrl}
            icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
            data-test-selector="cartLink"
            disabled={userRole === "Collaborator"}
            className={userRole === "Collaborator" ? "disabled" : ""}
            onClick={cartLink}
        >
            <Hidden below="lg" as="span" {...styles.cartTextLinkStyles}>
                {showLabel && `${translate("CART").toLowerCase()} `}
                <Typography as="span" {...styles.desktopCountText} data-test-selector="cartLinkQuantity">
                    {retainedTotalCountToDisplay || 0}
                </Typography>
            </Hidden>
            <Hidden
                above="md"
                css={css`
                    @media (min-width: 992px) and (max-width: 1166px) {
                        display: block !important;
                    }
                `}
            >
                <Typography as="span" {...styles.mobileCountText}>
                    {retainedTotalCountToDisplay || 0}
                </Typography>
            </Hidden>
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
